import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Kentucky = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Filing in Kentucky – HVUT, IRP, IFTA & VIN Guide"
          description="Learn how to file IRS Form 2290 in Kentucky, meet HVUT deadlines, correct VINs, stay compliant with IRP and IFTA, and e-file easily with TaxZerone."
          keywords=""
          canonical="resources/excise-tax/kentucky-form-2290/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          IRS Form 2290 Filing in{' '}
          <span className="highlight-container">
            <span className="highlight">Kentucky</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your Heavy Vehicle Use Tax (HVUT) quickly and securely online
          with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=kentucky-form-2290`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/kentucky-form-2290/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Kentucky Form 2290 at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Who Must File:
                            </strong>{' '}
                            Kentucky truck owners and operators must file Form
                            2290 for heavy vehicles with a taxable gross weight
                            of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more
                            </strong>{' '}
                            operating on public highways.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Filing Deadline:
                            </strong>{' '}
                            File by the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              last day of the month
                            </strong>{' '}
                            following the vehicle’s first use month. For
                            vehicles first used in July, the annual deadline is
                            generally{' '}
                            <strong style={{ fontWeight: 600 }}>
                              August 31
                            </strong>
                            .
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Required Information:
                            </strong>{' '}
                            Keep your{' '}
                            <strong style={{ fontWeight: 600 }}>
                              EIN, VIN, taxable gross weight, first-used month,
                              and payment details
                            </strong>{' '}
                            ready before filing.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Payment Options:
                            </strong>{' '}
                            Pay HVUT through{' '}
                            <Link
                              href="/resources/electronic-funds-withdrawal-efw/"
                              sx={{ color: '#0202ff' }}
                            >
                              EFW
                            </Link>
                            ,{' '}
                            <Link
                              href="/resources/eftps/"
                              sx={{ color: '#0202ff' }}
                            >
                              EFTPS
                            </Link>
                            , credit/debit card, or check/money order.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Schedule 1:
                            </strong>{' '}
                            An IRS-stamped{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Schedule 1
                            </strong>{' '}
                            is required for Kentucky vehicle registration,
                            renewal, and IRP compliance.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Corrections & Refunds:
                            </strong>{' '}
                            File{' '}
                            <Link
                              href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                              sx={{ color: '#0202ff' }}
                            >
                              VIN corrections
                            </Link>
                            , amendments, or{' '}
                            <Link
                              href="/excise-tax/efile-irs-form-8849-schedule-6-online/"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8849 Schedule 6
                            </Link>{' '}
                            when vehicle details change or eligible refunds
                            apply.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Agricultural Vehicles:
                            </strong>{' '}
                            Farm vehicles driven{' '}
                            <strong style={{ fontWeight: 600 }}>
                              7,500 miles or less
                            </strong>{' '}
                            may qualify for suspended tax status but still
                            require Form 2290 filing.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>
                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      If you operate a heavy vehicle on Kentucky’s roads
                      weighing 55,000 pounds or more, you must file{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 2290
                      </Link>{' '}
                      and pay the{' '}
                      <span style={{ fontWeight: 600 }}>
                        Heavy Vehicle Use Tax (HVUT)
                      </span>
                      . Whether you’re an independent trucker in Bowling Green
                      or manage a fleet out of Lexington, filing Form 2290 is an
                      annual federal requirement—and it also plays a critical
                      role in registering your vehicle with Kentucky’s DMV.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This page breaks down everything Kentucky truck owners
                      need to know to stay compliant and on the road.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>

                  <List
                    sx={{
                      mb: 5,
                      border: '1px solid #ccc',
                      borderRadius: 2,
                    }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 2290 in Kentucky?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#important" sx={{ color: '#0202ff' }}>
                          Important due dates for Kentucky truckers
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#first" sx={{ color: '#0202ff' }}>
                          First-time Kentucky filers: What you need to know
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-step: How to file Form 2290 in Kentucky?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#paying" sx={{ color: '#0202ff' }}>
                          Paying your HVUT: Kentucky-friendly options
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to submit Form 2290 for vehicle registration?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#using" sx={{ color: '#0202ff' }}>
                          Using Schedule 1 for IRP and IFTA compliance in
                          Kentucky
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#vin" sx={{ color: '#0202ff' }}>
                          How to handle VIN errors and stamped Schedule 1
                          corrections?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#agricultural" sx={{ color: '#0202ff' }}>
                          Agricultural vehicles and HVUT in Kentucky
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What to do if you sell, destroy, or retire a truck?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file Form 2290 in Kentucky?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you operate a heavy vehicle with a gross weight of 55,000
                  pounds or more on public highways in Kentucky, you are
                  required to file IRS Form 2290 and pay the Heavy Vehicle Use
                  Tax (HVUT). This includes:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, ml: 0, '& ul, li': { mb: 1.5 } }}
                >
                  <li>
                    <Link
                      href="https://owneroperator2290.com/?ref=taxzerone"
                      sx={{ color: '#0202ff' }}
                    >
                      Owner-operators
                    </Link>{' '}
                    who register their vehicles in Kentucky
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Trucking companies</span>{' '}
                    operating within or through the state
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Agricultural vehicle owners
                    </span>{' '}
                    whose mileage exceeds 7,500 miles annually
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>New vehicle owners</span>{' '}
                    registering a truck with the Kentucky Transportation Cabinet
                    (KYTC).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you operate a heavy vehicle with a gross weight of 55,000
                  pounds or more on public highways in Kentucky, you are
                  required to file IRS Form 2290 and pay the Heavy Vehicle Use
                  Tax (HVUT). This includes:
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="important"
                >
                  Important due date for Kentucky truckers
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The HVUT follows fiscal tax year from July 1 to June 30. The
                  IRS requires that you file Form 2290 by the last day of the
                  month following the month in which the vehicle is first used
                  on public highways.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '18px !important',
                  }}
                >
                  Key deadlines:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Annual Deadline:</span>{' '}
                    August 31* for vehicles in use as of July.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>First-Time Use:</span>{' '}
                    File by the end of the following month after the vehicle is
                    put into use.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If your
                  vehicle is first used in{' '}
                  <span style={{ fontWeight: 600 }}>October</span>, your filing
                  deadline is{' '}
                  <span style={{ fontWeight: 600 }}>November 30</span>.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  *If the deadline falls on the federal holiday or weekend, it
                  is then extended to the next business days.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="first"
                >
                  First-time Kentucky filers: what you need to know?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you're filing Form 2290 for the first time, you’ll need:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>A valid Employer Identification Number (EIN).</li>
                  <li>Your Vehicle Identification Number (VIN)</li>
                  <li>The gross taxable weight of your vehicle </li>
                  <li>
                    A method to pay your tax (
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      EFW
                    </Link>
                    ,{' '}
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      EFTPS
                    </Link>
                    , card, etc.)
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have all the above, you're ready to e-file and
                  receive your IRS stamped Schedule 1.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Step-by-step: How to file Form 2290 in Kentucky?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Filing Form 2290 in Kentucky can be done electronically or by
                  mail, but e-filing is the preferred method due to faster
                  processing.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="handle"
                >
                  Electronic filing (e-filing):
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing is the fastest, most convenient way to file IRS Form
                  2290, and it's required if you're reporting and paying tax on
                  25 or more taxable vehicles. This requirement does not include
                  tax-suspended vehicles (designated as Category W), since no
                  tax is due on them. However, the IRS encourages all
                  filers—regardless of fleet size—to file electronically.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="errors"
                >
                  Paper filing Form 2290 in Kentucky
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  While e-filing is strongly recommended, the IRS still allows{' '}
                  <span style={{ fontWeight: 600 }}>
                    paper filing of Form 2290
                  </span>{' '}
                  under certain conditions. Kentucky-based truck owners may
                  choose to paper file if they are:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Filing for{' '}
                    <span style={{ fontWeight: 600 }}>
                      fewer than 25 vehicles{' '}
                    </span>
                  </li>
                  <li>Unable to access the internet or e-filing services </li>
                  <li>
                    Required to file with special attachments (e.g., VIN
                    corrections, claims){' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="claim"
                >
                  Where to send Paper Form 2290?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you choose to file by paper, you must mail your completed{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    2290 tax form
                  </Link>{' '}
                  to the IRS at the address listed in the instructions (based on
                  whether you are including payment or not).
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Mailing Addresses">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#cfcfcf' }}>
                          <TableCell
                            align="center"
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            With payment
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 'bold',
                            }}
                          >
                            Without payment
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            International payment
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#f7f7f7' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Internal Revenue Service
                            <br />
                            P.O. Box 932500
                            <br />
                            Louisville, KY 40293-2500
                          </TableCell>

                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Department of the Treasury
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0031
                          </TableCell>

                          <TableCell>
                            Internal Revenue Service
                            <br />
                            International Accounts
                            <br />
                            1973 Rulon White Blvd.
                            <br />
                            Ogden, UT 84201-0038
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Important:</b> Paper filing is{' '}
                  <span style={{ fontWeight: 600 }}>much slower</span> than
                  e-filing. It can take several weeks to receive your stamped
                  Schedule 1, which may delay your Kentucky vehicle registration
                  or renewal.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="paying"
                >
                  Paying your HVUT tax online
                </Typography>
                <Typography variant="body1">
                  You can pay the Heavy Vehicle Use Tax using one of the
                  following IRS-approved methods:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      Electronic Funds Withdrawal
                    </Link>{' '}
                    (EFW) – Deducted directly from your bank account during
                    <br />
                    e-filing.
                  </li>
                  <li>
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      Electronic Federal Tax Payment System
                    </Link>{' '}
                    (EFTPS) – It is a free and secure option to pay your federal
                    taxes online.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Credit/Debit Card</span> –
                    Pay online through IRS-approved third-party processors.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Check or Money Order
                    </span>{' '}
                    – Must be mailed with Form 2290-V (Payment Voucher){' '}
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderLeft: '3px solid rgb(0, 70, 102)', mb: 3 }}
                >
                  <b>Tip:</b> For fastest processing, use EFW or EFTPS when
                  e-filing with TaxZerone.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="where"
                >
                  Where to submit Form 2290 for vehicle registration?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When registering or renewing a heavy vehicle in Kentucky, you{' '}
                  <span style={{ fontWeight: 600 }}>must submit the </span>
                  <Link
                    href="/resources/excise-tax/form-2290-schedule-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS‑stamped Schedule €¯1
                  </Link>{' '}
                  as official proof of Heavy Vehicle Use Tax (HVUT) payment.
                  Without it, registration cannot be completed:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit Schedule €¯1 to the Kentucky Division of Motor
                    Vehicle Licensing or your local County Clerk’s office.
                  </li>
                  <li>
                    Kentucky{' '}
                    <span style={{ fontWeight: 600 }}>
                      verifies HVUT compliance
                    </span>{' '}
                    by checking that the Schedule €¯1 includes all required VINs
                    and covers the correct tax period.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="using"
                >
                  Using schedule 1 for IRP and IFTA compliance in Kentucky
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Kentucky is part of the{' '}
                  <span style={{ fontWeight: 600 }}>
                    International Registration Plan (
                    <Link
                      href="/resources/excise-tax/irp/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRP
                    </Link>
                    ) and International Fuel Tax Agreement (
                    <Link
                      href="/resources/excise-tax/international-fuel-tax-agreement/"
                      sx={{ color: '#0202ff' }}
                    >
                      IFTA
                    </Link>
                    )
                  </span>
                  . To stay compliant:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit your stamped Schedule 1 during{' '}
                    <span style={{ fontWeight: 600 }}>IRP renewal</span>.
                  </li>
                  <li>
                    Keep proof of HVUT payment for{' '}
                    <span style={{ fontWeight: 600 }}>IFTA audits</span>.
                  </li>
                  <li>
                    Use the same EIN and vehicle details on all tax forms for
                    consistency.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="vin"
                >
                  How to handle VIN errors and stamped schedule 1 corrections?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your{' '}
                  <span style={{ fontWeight: 600 }}>VIN is incorrect</span> on a
                  submitted Form 2290:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You must file a{' '}
                    <Link
                      href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      VIN correction
                    </Link>
                    —TaxZerone makes this quick, easy and completely{' '}
                    <span style={{ fontWeight: 600 }}>free</span>.
                  </li>
                  <li>
                    You’ll receive a{' '}
                    <span style={{ fontWeight: 600 }}>new Schedule 1</span> with
                    the corrected VIN.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Other errors like filing for the wrong weight category may
                  require{' '}
                  <span style={{ fontWeight: 600 }}>
                    amendments or refiling
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="agricultural"
                >
                  Agricultural vehicles and HVUT in Kentucky
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Vehicles used{' '}
                  <span style={{ fontWeight: 600 }}>
                    exclusively for farming
                  </span>{' '}
                  may qualify for{' '}
                  <span style={{ fontWeight: 600 }}>suspended tax status</span>{' '}
                  if:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    They travel{' '}
                    <span style={{ fontWeight: 600 }}>7,500 miles or less</span>{' '}
                    annually.
                  </li>
                  <li>
                    They are{' '}
                    <span style={{ fontWeight: 600 }}>
                      registered as agricultural
                    </span>{' '}
                    vehicles.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must still{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 2290
                  </Link>{' '}
                  and indicate the{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                    sx={{ color: '#0202ff' }}
                  >
                    suspended
                  </Link>{' '}
                  status to stay compliant and receive your Schedule 1
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What to do if you sell, destroy, or retire a Truck?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a vehicle is:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, fontWeight: 600, '& li': { mb: 1.2 } }}
                >
                  <li>Sold</li>
                  <li>Destroyed</li>
                  <li>Stolen </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You may claim a{' '}
                  <span style={{ fontWeight: 600 }}>credit or refund</span> of
                  the unused portion of HVUT by filing{' '}
                  <Link
                    href="/resources/excise-tax/form-8849-schedule-6-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8849
                  </Link>{' '}
                  Schedule 6.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>A copy of the original Schedule 1 </li>
                  <li>Details of the vehicle disposition </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source & Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information on this page is based on official
                        guidance from the Internal Revenue Service (
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i2290.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>
                        )<br /> and the Kentucky Transportation Cabinet,
                        including IRS Instructions for Form 2290,{' '}
                        <Link
                          href="https://drive.ky.gov/Motor-Carriers/Pages/IRP.aspx"
                          sx={{ color: '#0202ff' }}
                        >
                          Kentucky IRP Registration Info
                        </Link>
                        , and{' '}
                        <Link
                          href="https://drive.ky.gov/Motor-Carriers/Pages/IFTA.aspx"
                          sx={{ color: '#0202ff' }}
                        >
                          Kentucky IFTA Requirements
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file your Kentucky Form 2290 quickly?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              File with TaxZerone and get your stamped Schedule 1 in minutes
              with the IRS watermark.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=kentucky-form-2290`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Kentucky;
